import { getFormLabel, getFormPlaceholder } from '@/shared/copy/forms';
import { Stack, Text } from '@/ui';
import {
  Button,
  Checkbox,
  Divider,
  IconButton,
  InputAdornment,
  TextField,
  VisibilityIcon,
} from '@/ui/primitives';
import { Box } from '@/ui/primitives/Box';
import { GoogleMark } from '../GoogleMark';
import { SignInScreenFrame } from '../sign-in/SignInScreenFrame';
import {
  authControlHeight,
  darkAuthLinkSx,
  darkAuthTextFieldSx,
  darkDividerSx,
  darkDividerTextSx,
  darkFieldLabelSx,
  darkForgotPasswordLinkSx,
  darkFooterTextSx,
  darkGoogleButtonSx,
  darkPasswordIconSx,
  darkPrimaryButtonSx,
} from '../sign-in/signInScreenPrimaryContentStyles';
import { authFieldLabelSx, authSecondaryTextSx, authTextFieldSx } from './authFieldStyles';
import { authPanelNarrowSx, authPanelShellMaxWidth, authPanelWideSx } from './authScreenLayout';
import type { ReactElement } from 'react';

const authInputHeight = 40;

const disabledAuthLinkSx = {
  color: 'primary.light',
  pointerEvents: 'none',
  textDecoration: 'none',
} as const;

const renderFieldLabel = (label: string): ReactElement => (
  <Box component="span" sx={authFieldLabelSx}>
    {label}
  </Box>
);

const renderSignInFallback = (): ReactElement => (
  <Stack component="form" gap="lg">
    <Stack gap="xl">
      <Stack gap="lg">
        <TextField
          label={
            <Box component="span" sx={[authFieldLabelSx, darkFieldLabelSx]}>
              {getFormLabel('email')}
            </Box>
          }
          inputHeight={authControlHeight}
          placeholder={getFormPlaceholder('email')}
          disabled={true}
          sx={darkAuthTextFieldSx}
        />
        <Stack gap="xs">
          <Stack direction="row" gap="sm" alignItems="center" justifyContent="space-between">
            <Text role="body" sx={darkFieldLabelSx}>
              {getFormLabel('password')}
            </Text>
            <Box component="span" sx={darkForgotPasswordLinkSx}>
              Forgot password?
            </Box>
          </Stack>
          <TextField
            label=""
            inputHeight={authControlHeight}
            placeholder={getFormPlaceholder('password')}
            type="password"
            disabled={true}
            sx={darkAuthTextFieldSx}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="Show password"
                      edge="end"
                      disabled={true}
                      sx={darkPasswordIconSx}
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />
        </Stack>
      </Stack>
      <Button type="button" disabled={true} sx={darkPrimaryButtonSx}>
        Sign In
      </Button>
    </Stack>
    <Stack gap="lg">
      <Stack direction="row" gap="md" alignItems="center">
        <Divider sx={darkDividerSx} />
        <Text role="body" sx={darkDividerTextSx}>
          or
        </Text>
        <Divider sx={darkDividerSx} />
      </Stack>
      <Button
        type="button"
        variant="secondary"
        startIcon={<GoogleMark />}
        disabled={true}
        sx={darkGoogleButtonSx}
      >
        Continue with Google
      </Button>
      <Text role="body" sx={darkFooterTextSx}>
        New to FlowQA?{' '}
        <Box component="span" sx={darkAuthLinkSx}>
          Create an account
        </Box>
      </Text>
    </Stack>
  </Stack>
);

const renderSignUpFallback = (): ReactElement => (
  <Stack component="form" gap="sm">
    <Stack gap="md">
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        gap="md"
        sx={{ '& > *': { flex: 1, minWidth: 0 } }}
      >
        <TextField
          label={renderFieldLabel(getFormLabel('firstName'))}
          inputHeight={authInputHeight}
          placeholder={getFormPlaceholder('firstName')}
          disabled={true}
          sx={authTextFieldSx}
        />
        <TextField
          label={renderFieldLabel(getFormLabel('lastName'))}
          inputHeight={authInputHeight}
          placeholder={getFormPlaceholder('lastName')}
          disabled={true}
          sx={authTextFieldSx}
        />
      </Stack>
      <TextField
        label={renderFieldLabel(getFormLabel('email'))}
        inputHeight={authInputHeight}
        placeholder={getFormPlaceholder('email')}
        disabled={true}
        sx={authTextFieldSx}
      />
      <TextField
        label={renderFieldLabel(getFormLabel('password'))}
        inputHeight={authInputHeight}
        placeholder={getFormPlaceholder('password')}
        type="password"
        disabled={true}
        sx={authTextFieldSx}
      />
    </Stack>
    <Stack gap="md">
      <Stack direction="row" gap="xs" alignItems="center" justifyContent="center">
        <Text role="body" color="textSecondary" sx={authSecondaryTextSx}>
          I agree to the{' '}
          <Box component="span" sx={disabledAuthLinkSx}>
            Terms of Service
          </Box>{' '}
          ,{' '}
          <Box component="span" sx={disabledAuthLinkSx}>
            Privacy Policy
          </Box>{' '}
          and{' '}
          <Box component="span" sx={disabledAuthLinkSx}>
            Acceptable Use Policy
          </Box>
        </Text>
        <Checkbox
          size="small"
          checked={false}
          disabled={true}
          slotProps={{
            input: {
              'aria-label': 'Accept terms of service, privacy policy, and acceptable use policy',
            },
          }}
        />
      </Stack>
      <Button type="button" disabled={true}>
        Sign Up
      </Button>
      <Text role="body" color="textSecondary" sx={authSecondaryTextSx}>
        Already have an account?{' '}
        <Box component="span" sx={disabledAuthLinkSx}>
          Sign in
        </Box>
      </Text>
      <Button type="button" variant="secondary" startIcon={<GoogleMark />} disabled={true}>
        Sign Up with Google
      </Button>
    </Stack>
  </Stack>
);

export const AuthRouteTransitionFallback = ({
  title = 'Loading',
}: {
  title?: string;
}): ReactElement => {
  const isSignUp = title === 'Sign Up';
  const panelSx = isSignUp ? authPanelWideSx : authPanelNarrowSx;

  return (
    <SignInScreenFrame
      title={title}
      sessionExpiredNotice={false}
      onDismissSessionExpired={() => undefined}
      shellMaxWidth={authPanelShellMaxWidth}
      panelSx={panelSx}
      variant={isSignUp ? 'default' : 'darkSignIn'}
    >
      {isSignUp ? renderSignUpFallback() : renderSignInFallback()}
    </SignInScreenFrame>
  );
};
